import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import "../../../css/CRUDModal.css";
import ButtonTriangle from "../../../assets/Icons/Button Triangle.png";
import CustomAlert from "../../../helpers/CustomAlert";
import {
  axiosGetApi,
  axiosPostApi,
} from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import FilterIcon from "../../../assets/Icons/Orange Filter.png";
import CrossIcon from "../../../assets/Icons/cross_svg.svg";
import { format, parse } from "date-fns";
import { formatAmount, formatDate } from "../../../helpers/helper";
import Dropdown from "../../../components/Dropdown";

const categoryOptions = [
  { value: "RECEIVER_METRO", label: "Receiver Metro" },
  { value: "REST_OF_INDIA", label: "Rest of India" },
  { value: "SPECIAL_INDIA", label: "Special India" },
  { value: "WITHIN_CITY", label: "Within City" },
  { value: "WITHIN_METRO", label: "Within Metro" },
  { value: "WITHIN_ZONE", label: "Within Zone" },
].sort((a, b) =>
  a.label.trim().localeCompare(b.label.trim(), undefined, {
    sensitivity: "base",
  })
);

const expressTypeOptions = [
  { value: "AIR", label: "Air" },
  { value: "SURFACE", label: "Surface" },
];

const ChargeMasterFilterModal = ({
  show,
  handleClose,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
  filterData,
  setFilterData,
  handleReset,
  setSmsData,
  setCurrentPage,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: {
      ...filterData,
      fromDate: filterData.fromDate || "",
      toDate: filterData.toDate || "",
      source: filterData.source || "",
      express: filterData.express || "",
      rateCardId: filterData.rateCardId || "",
      rateCardName: filterData.rateCardName || "",
    },
    mode: "onChange",
  });

  // Watch all form values to check if a filter has a value
  const watchedValues = watch();

  // Function to check if a tab has an active filter value
  const hasActiveFilter = (tabName) => {
    switch (tabName) {
      case "Category":
        return filterData?.source && filterData.source !== "";
      case "Card Type":
        return filterData?.rateCardId && filterData.rateCardId !== "";
      case "Express Type":
        return filterData?.express && filterData.express !== "";
      default:
        return false;
    }
  };

  const [selectedTab, setSelectedTab] = useState("Category");
  const [loading, setLoading] = useState(false);
  const loggedInUserName = sessionStorage.getItem("username");
  const [cardOptions, setCardOptions] = useState([]);

  const categoryDropdownOptions = useMemo(() => {
    const currentSource = watchedValues?.source || filterData?.source || "";
    if (currentSource) {
      return [
        { label: "✕ Clear Category Selection", value: "" },
        ...categoryOptions,
      ];
    }
    return categoryOptions;
  }, [watchedValues?.source, filterData?.source]);

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const onSubmit = (data) => {
    setFilterData(data);
    fetchPaymentHistoryData(1, data);
    setCurrentPage(1);
  };

  const fetchPaymentHistoryData = async (page, formData) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,

        ...(formData.fromDate && {
          fromDate: formatDate(formData.fromDate),
        }),

        ...(formData.toDate && {
          toDate: formatDate(formData.toDate),
        }),

        ...(formData?.source && { source: formData.source }),
        ...(formData?.express && { express: formData.express }),
        ...(formData?.rateCardId && { rateCardId: formData.rateCardId }),
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_CHARGES_MASTER}?pageSize=${pageSize}&pageNo=${page}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setSmsData(modifiedData);
        setNoData(false);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        setNoData(true);
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    const alignRight = (value) => (
      <div
        style={{
          textAlign: "center",
          paddingRight: "12px",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
    );

    return response?.map((item, index) => ({
      ...item,
      weight: alignRight(formatAmount(item?.weight)),
      height: alignRight(formatAmount(item?.height)),
      length: alignRight(formatAmount(item?.length)),
      width: alignRight(formatAmount(item?.width)),
      amount: (
        <div
          style={{
            textAlign: "right",
            paddingRight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.amount)}
        </div>
      ),

      fuelCharges: (
        <div
          style={{
            textAlign: "right",
            paddingRight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.fuelCharges)}
        </div>
      ),

      totalCharges: (
        <div
          style={{
            textAlign: "right",
            paddingRight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.totalCharges)}
        </div>
      ),

      additionalRate: (
        <div
          style={{
            textAlign: "right",
            paddingRight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.additionalRate ? item?.additionalRate : 0)}
        </div>
      ),

      additionalWeightUnit: (
        <div
          style={{
            width: "81px",
            textAlign: "end",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(
            item?.additionalWeightUnit ? item?.additionalWeightUnit : 0,
          )}
        </div>
      ),

      weightE: item?.weight,
      heightE: item?.height,
      lengthE: item?.length,
      widthE: item?.width,
      amountE: item?.amount,
      fuelChargesE: item?.fuelCharges,
      totalChargesE: item?.totalCharges,
      additionalRateE: item?.additionalRate,
      additionalWeightUnitE: item?.additionalWeightUnit,
    }));
  };

  useEffect(() => {
    if (!show) return;
    fetchCardData();
  }, [show]);

  const fetchCardData = async () => {
    try {
      const response = await axiosGetApi(
        `${apiList.ALL_RATE_CARD_DATA}?username=${loggedInUserName}`,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        const sorted = (response.data.data || []).sort((a, b) =>
          (a.rateCardName || "").trim().localeCompare((b.rateCardName || "").trim(), undefined, { sensitivity: "base" })
        );
        setCardOptions(sorted);
      }
    } catch (err) {
      console.error("Item fetch error", err);
    }
  };

  const onReset = () => {
    reset({
      source: "",
      express: "",
      rateCardId: "",
      rateCardName: "",
    });
    setFilterData({
      source: "",
      express: "",
      rateCardId: "",
      rateCardName: "",
    });
    handleReset();
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="leftImg"
                className="absolute right-0 top-0 w-[110px] h-[105px]"
              />
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <div className="flex items-center gap-2">
                  <img src={FilterIcon} alt="filter icon" className="w-6 h-6" />
                  <h5 className="modal-title main-title font-medium">
                    Filters
                  </h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="filter icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleClose}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {["Category", "Card Type", "Express Type"].map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                            style={
                              hasActiveFilter(tab) && selectedTab !== tab
                                ? {
                                    outline: "2px solid #FF6B35",
                                    outlineOffset: "2px",
                                  }
                                : {}
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content !bg-[linear-gradient(to_bottom,#FFE8D4_0%_100%,transparent_25%_100%)]">
                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value, formValues) =>
                                  !value ||
                                  !formValues.toDate ||
                                  new Date(value) <=
                                    new Date(formValues.toDate) ||
                                  "From Date must be before To Date",
                                requiredIfToDate: (value, formValues) =>
                                  !formValues.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        fromDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <div className="error-text">
                                      {errors.fromDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value, formValues) =>
                                  !value ||
                                  !formValues.fromDate ||
                                  new Date(value) >=
                                    new Date(formValues.fromDate) ||
                                  "To Date must be after From Date",
                                requiredIfFromDate: (value, formValues) =>
                                  !formValues.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        toDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.toDate && (
                                    <div className="error-text">
                                      {errors.toDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {selectedTab === "Card Type" && (
                        <div className="dateField">
                          <label
                            className="dateLabel"
                            style={{
                              marginBottom: "0.5rem",
                              fontWeight: "600",
                              fontSize: "20px",
                            }}
                          >
                            Card Type
                          </label>

                          <Controller
                            name="rateCardId"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                label=""
                                options={cardOptions}
                                value={field.value}
                                onChange={(item) => {
                                  field.onChange(item.rateCardId);
                                  setValue("rateCardName", item.rateCardName);

                                  setFilterData((prev) => ({
                                    ...prev,
                                    rateCardId: item.rateCardId,
                                    rateCardName: item.rateCardName,
                                  }));
                                }}
                                labelKey="rateCardName"
                                valueKey="rateCardId"
                                placeholder="Select Card Type"
                                isRequired={false}
                              />
                            )}
                          />
                        </div>
                      )}

                      {selectedTab === "Category" && (
                        <div className="dateField">
                          <label
                            className="dateLabel"
                            style={{
                              marginBottom: "0.5rem",
                              fontWeight: "600",
                              fontSize: "20px",
                            }}
                          >
                            Category
                          </label>

                          <Controller
                            name="source"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                label=""
                                options={categoryDropdownOptions}
                                value={
                                  field.value ||
                                  watchedValues?.source ||
                                  filterData?.source ||
                                  ""
                                }
                                showSearch={true}
                                onChange={(item) => {
                                  const val = item ? item.value || "" : "";
                                  field.onChange(val);

                                  setFilterData((prev) => ({
                                    ...prev,
                                    source: val,
                                  }));
                                }}
                                placeholder="Select Category"
                                isRequired={false}
                              />
                            )}
                          />
                        </div>
                      )}

                      {selectedTab === "Express Type" && (
                        <div className="dateField">
                          <label
                            className="dateLabel"
                            style={{
                              marginBottom: "0.5rem",
                              fontWeight: "600",
                              fontSize: "20px",
                            }}
                          >
                            Express Type
                          </label>

                          <Controller
                            name="express"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                label=""
                                options={expressTypeOptions}
                                value={field.value}
                                onChange={(item) => {
                                  field.onChange(item.value);

                                  setFilterData((prev) => ({
                                    ...prev,
                                    express: item.value,
                                  }));
                                }}
                                placeholder="Select Express Type"
                                isRequired={false}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back w-[133px] !rounded-lg"
                      onClick={onReset}
                    >
                      Clear
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit w-[133px] !rounded-lg"
                      disabled={isSubmitting}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChargeMasterFilterModal;
